import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { EVENTOS, capturarOrigen, track } from "@/lib/captacion";
import { cn } from "@/lib/utils";
import { Aparece } from "./Aparece";
import { Boton } from "./Boton";
import { LEMA, PLANES, RANGOS_PROPIEDADES, whatsappUrl } from "./content";
import { MarcaDeAgua } from "./MarcaDeAgua";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const VACIO = { nombre: "", correo: "", agencia: "", propiedades: "", telefono: "", plan: "", sitio_web: "" };

const campo =
  "mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-base text-slate-900 placeholder:text-slate-400 transition-colors duration-200 focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-100 sm:text-sm";
const rotulo = "block text-sm font-semibold text-slate-700";

// Formulario corto: nombre, correo, agencia, tamaño de cartera (en rangos, para calificar sin
// pedir un número exacto) y teléfono opcional. `sitio_web` es un honeypot: invisible para personas,
// los bots lo llenan y el backend descarta el envío sin avisarles.
export const DemoForm = ({ planInteres }) => {
  const [datos, setDatos] = useState(VACIO);
  const [estado, setEstado] = useState("idle"); // idle | enviando | ok | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (planInteres) setDatos((d) => ({ ...d, plan: planInteres }));
  }, [planInteres]);

  const set = (k) => (e) => setDatos((d) => ({ ...d, [k]: e.target.value }));

  const enviar = async (e) => {
    e.preventDefault();
    setEstado("enviando");
    setError("");
    try {
      const resp = await fetch(`${BACKEND}/api/public/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...datos, origen: capturarOrigen() }),
      });
      if (!resp.ok) {
        const cuerpo = await resp.json().catch(() => ({}));
        throw new Error(typeof cuerpo.detail === "string" ? cuerpo.detail : "");
      }
      setEstado("ok");
      track(EVENTOS.lead, { content_name: datos.plan || "demo" });
    } catch (err) {
      setEstado("error");
      setError(err.message || "No pudimos enviar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.");
    }
  };

  return (
    <section id="demo" className="relative scroll-mt-16 bg-white" data-testid="landing-demo">
      <MarcaDeAgua className="h-48 text-indigo-900 opacity-[0.05] sm:h-64" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:py-28">
        <Aparece>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{LEMA}</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Mira SEBBIA con casos como los tuyos.
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">
            Déjanos tus datos y te contactamos para coordinar una videollamada. Te mostramos cómo quedaría tu cierre de
            mes y resolvemos tus dudas.
          </p>
          <a
            href={whatsappUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track(EVENTOS.contacto, { canal: "whatsapp", seccion: "demo" })}
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-emerald-700"
          >
            <MessageCircle className="h-4 w-4 text-emerald-600" /> ¿Prefieres WhatsApp? Escríbenos
          </a>
        </Aparece>

        <Aparece className="rounded-2xl bg-white p-6 shadow-md ring-1 ring-inset ring-slate-200/60 sm:p-8">
          {estado === "ok" ? (
            <div className="py-6" role="status" data-testid="demo-ok">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </span>
              <p className="mt-5 font-display text-xl font-extrabold text-slate-900">¡Listo, {datos.nombre.split(" ")[0]}!</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Recibimos tu solicitud. Te escribiremos a {datos.correo} para coordinar la demo.
              </p>
            </div>
          ) : (
            <form onSubmit={enviar} noValidate={false} data-testid="demo-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className={rotulo}>
                  Nombre
                  <input className={campo} name="nombre" autoComplete="name" required maxLength={120} value={datos.nombre} onChange={set("nombre")} />
                </label>
                <label className={rotulo}>
                  Correo
                  <input className={campo} name="correo" type="email" autoComplete="email" required maxLength={160} value={datos.correo} onChange={set("correo")} />
                </label>
                <label className={rotulo}>
                  Corredora o agencia
                  <input className={campo} name="agencia" autoComplete="organization" required maxLength={160} value={datos.agencia} onChange={set("agencia")} />
                </label>
                <label className={rotulo}>
                  Propiedades que administras
                  <select className={campo} name="propiedades" required value={datos.propiedades} onChange={set("propiedades")}>
                    <option value="" disabled>Elige un rango</option>
                    {RANGOS_PROPIEDADES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className={rotulo}>
                  Teléfono <span className="font-normal text-slate-400">(opcional)</span>
                  <input className={campo} name="telefono" type="tel" autoComplete="tel" maxLength={30} placeholder="+56 9" value={datos.telefono} onChange={set("telefono")} />
                </label>
                <label className={rotulo}>
                  Plan de interés <span className="font-normal text-slate-400">(opcional)</span>
                  <select className={campo} name="plan" value={datos.plan} onChange={set("plan")}>
                    <option value="">Aún no sé</option>
                    {PLANES.map((p) => <option key={p.key} value={p.nombre}>{p.nombre}</option>)}
                  </select>
                </label>
              </div>
              <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
                <label>
                  Sitio web
                  <input name="sitio_web" tabIndex={-1} autoComplete="off" value={datos.sitio_web} onChange={set("sitio_web")} />
                </label>
              </div>

              {estado === "error" && (
                <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-inset ring-red-100" role="alert">
                  {error}
                </p>
              )}

              <Boton
                as="button"
                type="submit"
                tamano="lg"
                disabled={estado === "enviando"}
                className={cn("mt-6 w-full")}
                data-testid="demo-enviar"
              >
                {estado === "enviando" ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</>
                ) : (
                  <>Agenda una demo <ArrowRight className="h-4 w-4" /></>
                )}
              </Boton>
              <p className="mt-4 text-xs leading-relaxed text-slate-500">
                Usamos tus datos sólo para contactarte por esta solicitud. Revisa nuestra{" "}
                <a href="/privacidad" className="font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-indigo-700">
                  política de privacidad
                </a>.
              </p>
            </form>
          )}
        </Aparece>
      </div>
    </section>
  );
};
